'use strict';

// Must match delegate-handle="documentPages" on the pages ion-scroll in documentWindow.html.
const DOCUMENT_PAGES_SCROLL_HANDLE = 'documentPages';

/**
 * Service for document printing and opening functionality
 */
export class DocumentPrintHelper {
  static $inject = ['documentsSvc', '$ionicLoading', 'modalService', 'techpadConfig', '$rootScope', 'techpadInitialConfigProvider', '$ionicScrollDelegate'];

  constructor(documentsSvc, $ionicLoading, modalService, techpadConfig, $rootScope, techpadInitialConfigProvider, $ionicScrollDelegate) {
    this.documentsSvc = documentsSvc;
    this.$ionicLoading = $ionicLoading;
    this.modalService = modalService;
    this.techpadConfig = techpadConfig;
    this.$rootScope = $rootScope;
    this.techpadInitialConfigProvider = techpadInitialConfigProvider;
    this.$ionicScrollDelegate = $ionicScrollDelegate;
  }

  /**
   * Prints a document on iOS devices
   * @param {Object} document - The document object to print
   * @param {boolean} isServerPrintOnly - Whether to only use server printers
   */
  async iosPrint(document, isServerPrintOnly) {

    try {
      this.$ionicLoading.show({
        template: 'Preparing for printing...'
      });

      if (this.techpadConfig.settings.ServerPrinterEnabled) {
        this.$ionicLoading.hide();
        
        this.$ionicLoading.show({
          template: 'Loading printers...'
        });
        
        let response;
        try {
          response = await this.documentsSvc.getEnabledPrinters();
        } catch (error) {
          this.modalService.showErrorMessage(error.data || 'Error loading printers.', error.status);
          return;
        } finally {
          this.$ionicLoading.hide();
        }

        const printers = response.data;
        
        if (printers.length > 1) {
          this.modalService.showEnabledPrinters(document, printers);
        } else if (printers.length === 1) {
          this.$ionicLoading.show({
            template: 'Printing document...'
          });

          try {
            await this.documentsSvc.printDocument(document);
            this.modalService.showAlert('Document sent to server printer.');
            if (document.onPrintedCallback) {
              document.onPrintedCallback();
            }
          } catch (error) {
            this.modalService.showErrorMessage(error.data || 'Error printing document.', error.status);
          } finally {
            this.$ionicLoading.hide();
          }
        }
      } else {
        if (isServerPrintOnly) {
          this.modalService.showAlert('Server printers are not defined.');
          this.$ionicLoading.hide();
          return;
        }

        try {
          const url = await this.documentsSvc.getDocumentUrl(document);
          const fileTransfer = new FileTransfer();
          const uri = encodeURI(url);
          const localPath = `cdvfile://localhost/temporary/downloaded_${new Date().getTime()}.pdf`;

          fileTransfer.download(
            uri,
            localPath,
            (entry) => {
              this.$ionicLoading.hide();
              window.plugins.PrintPDF.print({
                data: localPath,
                type: 'File',
                title: 'Print Document',
                success: () => {
                  entry.remove();
                },
                error: () => {
                  entry.remove();
                }
              });
            },
            (error) => {
              this.$ionicLoading.hide();
              this.modalService.showAlert('Error downloading document for printing.');
            },
            false, {
              headers: {
                "Content-Type": "application/pdf"
              }
            }
          );
        } catch (error) {
          this.$ionicLoading.hide();
          this.modalService.showErrorMessage(error.data || 'Failed to get document URL for printing.', error.status);
        }
      }
    } catch (error) {
      this.$ionicLoading.hide();
      this.modalService.showErrorMessage(error.data || 'Unexpected error while printing.', error.status);
    } finally {
      this.$ionicLoading.hide();
    }
  }

  /**
   * Opens a document in Electron
   * @param {string} url - The document URL
   */
  openDocumentElectron(url) {
    if (this.techpadInitialConfigProvider && this.techpadInitialConfigProvider.electron) {
      url += "?fromElectron=true";
      this.techpadInitialConfigProvider.electron.openPdf(url);
      this.$ionicLoading.hide();
    }
  }

  /**
   * Opens a document in browser/mobile view
   * @param {string} url - The document URL
   * @param {Object} document - The document object
   * @param {Object} options - Additional options
   * @param {Object} options.patient - Patient object (optional)
   * @param {Function} options.iosPrintCallback - Custom iOS print callback (optional)
   */
  async openDocumentBrowser(url, document, options) {
    const { patient = null, iosPrintCallback = null } = options || {};

    const isImage = document.FileType === 'image';
    const isTxt = document.FileType === 'txt';
    const usePages = this.$rootScope.isIOS && !isImage && !isTxt;

    if (this.$rootScope.isIOS || isImage) {
      if (!usePages) {
        this.$ionicLoading.hide();
      }
      if (!isTxt) {
        url += `?asImage=true${isImage ? '&fromElectron=true' : ''}`;
      }
    }

    let pages = null;
    if (usePages) {
      try {
        pages = await this.documentsSvc.getDocumentPages(document);
      } catch (error) {
        this.$ionicLoading.hide();
        this.modalService.showErrorMessage((error && error.data) || 'Failed to load document.', error && error.status);
        return;
      } finally {
        this.$ionicLoading.hide();
      }

      if (!pages || !pages.length) {
        this.modalService.showErrorMessage('Document could not be displayed.');
        return;
      }
    }

    let documentModel = {
      fileType: document.FileType || 'pdf',
      url: url,
      pages: pages,
      patient: patient,
      asImage: isImage,
      loaded: () => {
        this.$ionicLoading.hide();
      },
      pageLoaded: () => {
        this.$ionicLoading.hide();
        this.$ionicScrollDelegate.$getByHandle(DOCUMENT_PAGES_SCROLL_HANDLE).resize();
      },
      techpadSettings: this.techpadConfig.settings,
      iosPrint: async () => {
        if (iosPrintCallback) {
          await iosPrintCallback();
        } else {
          await this.iosPrint(document);
        }
      },
      error: () => {
        this.$ionicLoading.hide();
      }
    };

    this.modalService.showDocumentWindow(documentModel);
  }
}




// WEBPACK FOOTER //
// ./src/scripts/helpers/documentPrintHelper.js